import { AnimateWinsComponent } from "ags/components/animate-wins-component";
import { AgsLayers } from "ags/names";
import { BigWinThresholds } from "ags/utils/win-magnitude";
import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { AnimatedSprite } from "appworks/graphics/animation/animated-sprite";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { Layers } from "appworks/graphics/layers/layers";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { gameState } from "appworks/model/game-state";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { stateMachine } from "appworks/state-machine/state-machine";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Timer } from "appworks/utils/timer";
import { Tween } from "appworks/utils/tween";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { Point } from "pixi.js";
import { Emitter } from "pixi-particles";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { LayerNames } from "game-layers";

export class BigWinComponent extends AbstractComponent {
    protected readonly bonusWinLabel = "bonus_win_label";
    protected readonly bonusWinBackgroundName = "bonus_win_background";
    protected readonly speedIn: number = 1.5;
    protected readonly speedOut: number = 0.3;
    protected readonly textScale: number = 1.15;

    protected borderTweens: Tween[] = [];
    protected rotationTween: Tween;
    protected bigWinUiLayer: Layers;
    protected bigWinLevel: BigWinThresholds;
    protected emitterName = "bigWinEmitter";
    protected winTextLabel = "win_label";
    protected winTextBackground = "win_bg";
    protected bigWinSceneName = "bigWinCoin";
    protected hugeWinSceneName = "bigWinGem";
    protected thresholdConfig: { [key: number]: { sceneName: string; winAnimationName: string; jumpTime: number; outroTime: number } } = {};
    protected bigWinAnimationName = "bigwin";
    protected megaWinAnimationName = "megawin";
    protected backdropName = "backdrop_overlay";
    protected winAnimationLooping = false;
    protected activeTweens: Tween[];
    protected textTweens: { in: (speed?: number) => void; out: (speed?: number) => void };
    protected winAmountTextName = "total_win_currency_value";
    protected winTickComplete = false;
    protected jumped = false;
    protected bigWinContract: Contract = undefined;

    constructor() {
        super();
    }

    get transactionService() {
        return Services.get(TransactionService);
    }

    get showerEmitter() {
        return Layers.get(LayerNames.CelebrationForeground).getParticle(this.emitterName);
    }

    get backdropSprite() {
        return Layers.get(LayerNames.CelebrationForeground).getSprite(this.backdropName);
    }

    get totalWinAmount() {
        const currentRecord = gameState.getCurrentGame().getCurrentRecord();

        return currentRecord.cashWon;
    }

    public start(bigWinLevel: BigWinThresholds) {
        this.bigWinLevel = bigWinLevel;
        this.setThresholds();
        this.bigWinUiLayer = Layers.get(AgsLayers.BIG_WIN_EMITTER);
        this.transactionService.totalWinUpdate.add(this.onWinUpdate, this);
        const isFreespins = !Layers.get(LayerNames.BetBar).getText(this.winTextLabel);

        if (isFreespins) {
            this.winTextLabel = this.bonusWinLabel;
            this.winTextBackground = this.bonusWinBackgroundName;
        }

        if (Object.prototype.hasOwnProperty.call(this.thresholdConfig, bigWinLevel)) {
            this.jumped = false;
            const { sceneName } = this.thresholdConfig[this.bigWinLevel];
            Layers.get(LayerNames.CelebrationForeground).setScene(sceneName).execute();
            this.setupTweens();
            this.setupCallbacks();
            uiFlags.set(UIFlag.BIG_WIN, true);
            this.startAnimation();
            this.startWinBoxEmitters();
            this.fadeOutWinText();
            this.startShowerEmitter();

            if (this.bigWinLevel >= BigWinThresholds.HUGE) {
                this.animateRays();
            }
        }
    }

    public coinSprayStart(bigWinLevel: BigWinThresholds) {
        this.bigWinLevel = bigWinLevel;
        this.setThresholds();
        this.bigWinUiLayer = Layers.get(AgsLayers.BIG_WIN_EMITTER);
        const isFreespins = !Layers.get(LayerNames.BetBar).getText(this.winTextLabel);

        if (isFreespins) {
            this.winTextLabel = this.bonusWinLabel;
            this.winTextBackground = this.bonusWinBackgroundName;
        }

        if (Object.prototype.hasOwnProperty.call(this.thresholdConfig, bigWinLevel)) {
            this.jumped = false;
            const { sceneName } = this.thresholdConfig[this.bigWinLevel];
            Layers.get(LayerNames.CelebrationForeground).setScene(sceneName).execute();
            this.fadeOut(this.backdropSprite, 0);
            uiFlags.set(UIFlag.BIG_WIN, true);
            this.startWinBoxEmitters();
            this.startShowerEmitter();
        }
    }

    public stop(immediateStop = false): Contract<void> {
        return new Contract((resolve) => {
            this.stopAnimation(immediateStop);
            this.cleanUpTextFields();
            this.stopShowerEmitter();
            this.stopWinBoxEmitters();
            this.hideRays();

            if (immediateStop) {
                if (Services.get(AutoplayService).isAutoPlaying()) {
                    stateMachine.skip();
                }
                uiFlags.set(UIFlag.BIG_WIN, false);
                resolve(null);
                return;
            }

            // Wait for particles to die.
            Timer.setTimeout(() => {
                if (Services.get(AutoplayService).isAutoPlaying()) {
                    stateMachine.skip();
                }
                uiFlags.set(UIFlag.BIG_WIN, false);
                resolve(null);
            }, 3000);
        });
    }

    public onWinUpdate(value: number) {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAmountText = celebrationLayer.getText(this.winAmountTextName);

        if (winAmountText ?? null) {
            if (this.winTickComplete) {
                winAmountText.text = this.formatFunction(this.totalWinAmount);
            } else {
                winAmountText.text = this.formatFunction(value);
            }
        }
        if (value === this.totalWinAmount) {
            this.winTickComplete = true;
        }
    }

    protected setThresholds() {
        this.thresholdConfig[BigWinThresholds.BIG] = {
            sceneName: this.bigWinSceneName,
            winAnimationName: this.bigWinAnimationName,
            jumpTime: 8.5,
            outroTime: 9.1
        };
        this.thresholdConfig[BigWinThresholds.HUGE] = {
            sceneName: this.hugeWinSceneName,
            winAnimationName: this.megaWinAnimationName,
            jumpTime: 47.7,
            outroTime: 48.3
        };
    }

    protected stopActiveTweens() {
        if (!this.activeTweens) {
            return;
        }

        for (const eachTween of this.activeTweens) {
            eachTween.stop();
        }

        this.activeTweens = [];
    }

    protected setupTweens() {
        const animationLoopTime = 500;
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAmountText = celebrationLayer.getText(this.winAmountTextName);
        this.textTweens = {
            in: (speed) => {
                speed = speed || 1;
                this.stopActiveTweens();
                const alphaTween = new Tween(winAmountText);
                const landscapeTween = new Tween(winAmountText.getPosition(Orientation.LANDSCAPE).scale);
                const portraitTween = new Tween(winAmountText.getPosition(Orientation.PORTRAIT).scale);
                this.activeTweens = [alphaTween, landscapeTween, portraitTween];
                winAmountText.landscape.scale.x = 0;
                winAmountText.landscape.scale.y = 0;
                winAmountText.portrait.scale.y = 0;
                winAmountText.portrait.scale.y = 0;
                winAmountText.alpha = 0;
                const duration = animationLoopTime / speed;

                alphaTween.to({ alpha: 1 }, duration);
                landscapeTween.to({ x: this.textScale, y: this.textScale }, duration);
                portraitTween.to({ x: this.textScale, y: this.textScale }, duration);

                alphaTween.start();
                landscapeTween.start();
                portraitTween.start();
            },
            out: (speed) => {
                speed = speed || 1;
                this.stopActiveTweens();
                const alphaTween = new Tween(winAmountText);
                const landscapeTween = new Tween(winAmountText.getPosition(Orientation.LANDSCAPE).scale);
                const portraitTween = new Tween(winAmountText.getPosition(Orientation.PORTRAIT).scale);
                this.activeTweens = [alphaTween, landscapeTween, portraitTween];
                winAmountText.landscape.scale.x = this.textScale;
                winAmountText.landscape.scale.y = this.textScale;
                winAmountText.portrait.scale.y = this.textScale;
                winAmountText.portrait.scale.y = this.textScale;
                winAmountText.alpha = 1;
                const duration = animationLoopTime * speed;

                alphaTween.delay(animationLoopTime);
                landscapeTween.delay(animationLoopTime);
                portraitTween.delay(animationLoopTime);

                alphaTween.to({ alpha: -1 }, duration);
                landscapeTween.to({ x: 0, y: 0 }, duration);
                portraitTween.to({ x: 0, y: 0 }, duration);

                alphaTween.start();
                landscapeTween.start();
                portraitTween.start();
            }
        };
    }

    protected setupCallbacks() {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAnimation = celebrationLayer.getSpine(this.thresholdConfig[this.bigWinLevel]?.winAnimationName);
        winAnimation.onEvent.removeAll();
        winAnimation.onEvent.add(this.onAnimEventCallback);
        winAnimation.onStart.removeAll();
        winAnimation.onStart.addOnce(this.onStartCallback);
        winAnimation.onComplete.removeAll();
        winAnimation.onComplete.addOnce(this.onCompleteCallback);
    }

    protected startAnimation() {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAmountText = celebrationLayer.getText(this.winAmountTextName);
        const winAnimation = celebrationLayer.getSpine(this.thresholdConfig[this.bigWinLevel]?.winAnimationName);
        this.winTickComplete = false;
        this.winAnimationLooping = true;
        this.bigWinContract = winAnimation.playOnce("animation", false, 1);
        this.bigWinContract.execute();
        winAnimation.visible = true;
        winAmountText.scaleX = 0;
        winAmountText.scaleY = 0;
        winAmountText.alpha = 0;
        winAmountText.text = this.formatFunction(0);
        this.stopWinBoxEmitters();
    }

    protected stopAnimation(immediateStop: boolean) {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAnimation = celebrationLayer.getSpine(this.thresholdConfig[this.bigWinLevel]?.winAnimationName);
        this.winAnimationLooping = false;
        this.transactionService.setWinnings(this.totalWinAmount).execute();
        this.transactionService.totalWinUpdate.remove(this.onWinUpdate, this);
        Components.get(AnimateWinsComponent).endBigWinAnimation(true).execute();
        Services.get(SoundService).customEvent("cancel_rollup");
        this.winTickComplete = true;

        if ((winAnimation?.isPlaying() ?? false) && immediateStop) {
            const winAmountText = celebrationLayer.getText(this.winAmountTextName);
            this.jumped = true;
            winAnimation.jump(this.thresholdConfig[this.bigWinLevel]?.jumpTime);
            this.stopActiveTweens();
            winAmountText.scaleX = this.textScale;
            winAmountText.scaleY = this.textScale;
            winAmountText.alpha = 1;
            this.onCompleteCallback(null, null, 1150);
        }

        uiFlags.set(UIFlag.BIG_WIN_DELAY, true);
        Timer.setTimeout(() => {
            uiFlags.set(UIFlag.BIG_WIN_DELAY, false);
        }, uiFlags.SLAM_STOP_DELAY);
    }

    protected onStartCallback: (value: any, value2: AnimatedSprite) => void = (data, winAnimation) => {
        this.textTweens.in(this.speedIn);
        this.fadeIn(this.backdropSprite, 1, 200);
    };

    protected onCompleteCallback: (value: any, value2: AnimatedSprite, value3?: number) => void = (data, winAnimation, delay = 300) => {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const spineContainer = celebrationLayer?.getSpine(this.thresholdConfig[this.bigWinLevel]?.winAnimationName);
        spineContainer?.setPlaying(false);

        Timer.setTimeout(() => {
            uiFlags.set(UIFlag.BIG_WIN, false);
            this.bigWinContract?.cancel();
            this.bigWinContract = undefined;
        }, delay);
    };

    protected onAnimEventCallback: (value: any, value2: any) => void = (data, winAnimation) => {
        const celebrationLayer = Layers.get(LayerNames.CelebrationForeground);
        const winAmountText = celebrationLayer.getText(this.winAmountTextName);

        if (winAnimation.data.name === "loop" && !this.jumped) {
            winAmountText.scaleX = this.textScale;
            winAmountText.scaleY = this.textScale;
            winAmountText.alpha = 1;
        } else if (winAnimation.data.name === "outro" && winAnimation.time < this.thresholdConfig[this.bigWinLevel]?.outroTime) {
            this.textTweens.out(this.speedOut);
            this.fadeOut(this.backdropSprite, 200);
        }
    };

    protected hideRays(): void {
        Layers.get(AgsLayers.RAYS).setScene("default").execute();
    }

    protected animateRays(): void {
        Layers.get(AgsLayers.RAYS).setScene("bigwin").execute();

        const rays = Layers.get(AgsLayers.RAYS).getSprite("rays");
        rays.visible = true;
        CenterPivot(rays);
        this.fadeIn(rays, 0.5, 1000);
        this.rotationTween = new Tween(rays);
        this.rotationTween.to({ rotation: Math.PI * 2 }, 40000);
        this.rotationTween.repeat(Infinity);
        this.rotationTween.start();
    }

    protected stopShowerEmitter() {
        if (this.showerEmitter) {
            this.showerEmitter.emit = false;
        }
    }

    protected startShowerEmitter() {
        this.showerEmitter.emit = true;
    }

    protected cleanUpTextFields(): void {
        Services.get(SoundService).customEvent("stopBigWins");
        Services.get(SoundService).customEvent("bigWinOutro");
        const betBar: Layers = Layers.get(LayerNames.BetBar);
        this.fadeIn(betBar.getText(this.winTextLabel));
        this.fadeIn(betBar.getText(this.winTextLabel));
        const backgroundSprite = betBar.getSprite(this.winTextBackground);
        if (backgroundSprite) {
            this.fadeIn(backgroundSprite);
        }
    }

    protected showWinText(): void {
        const betBar: Layers = Layers.get(LayerNames.BetBar);
        betBar.getText(this.winTextLabel).visible = true;
        const backgroundSprite = betBar.getSprite(this.winTextBackground);

        if (backgroundSprite) {
            backgroundSprite.visible = true;
        }
    }

    protected fadeOutWinText(): void {
        const betBar: Layers = Layers.get(LayerNames.BetBar);
        this.fadeOut(betBar.getText(this.winTextLabel));
        const backgroundSprite = betBar.getSprite(this.winTextBackground);

        if (backgroundSprite) {
            this.fadeOut(backgroundSprite);
        }
    }

    protected fadeIn(displayObject: PIXI.DisplayObject, targetAlpha = 1, duration = 300): void {
        if (!displayObject) {
            return;
        }

        displayObject.alpha = 0;
        const exitTween = new Tween(displayObject);
        exitTween.to({ alpha: targetAlpha }, duration);
        exitTween.start();
    }

    protected fadeOut(displayObject: PIXI.DisplayObject, duration = 300): void {
        if (!displayObject) {
            return;
        }

        const exitTween = new Tween(displayObject);
        exitTween.to({ alpha: 0 }, duration);
        exitTween.start();
    }

    protected startWinBoxEmitters(): void {
        if (this.bigWinUiLayer != null) {
            this.bigWinUiLayer.setScene("bigWin").execute();
            this.bigWinUiLayer.getParticle("bigWinBackground").emit = true;
            this.tweenBorderEmitter("borderLeftLandscape", "left", Orientation.LANDSCAPE);
            this.tweenBorderEmitter("borderRightLandscape", "right", Orientation.LANDSCAPE);
            this.tweenBorderEmitter("borderLeftPortrait", "left", Orientation.PORTRAIT);
            this.tweenBorderEmitter("borderRightPortrait", "right", Orientation.PORTRAIT);
        }
    }

    protected stopWinBoxEmitters(): void {
        if (this.bigWinUiLayer != null) {
            [
                this.bigWinUiLayer.getParticle("borderLeftLandscape"),
                this.bigWinUiLayer.getParticle("borderRightLandscape"),
                this.bigWinUiLayer.getParticle("borderLeftPortrait"),
                this.bigWinUiLayer.getParticle("borderRightPortrait"),
                this.bigWinUiLayer.getParticle("bigWinBackground")
            ].forEach((particle: Emitter) => {
                if (particle != null) {
                    particle.emit = false;
                }
            });
            this.borderTweens.forEach((tween) => {
                tween.stop();
            });
            this.borderTweens = [];
        }
    }

    protected tweenBorderEmitter(emitterName: string, direction: "left" | "right", orientation: Orientation): void {
        const emitter = this.bigWinUiLayer.getParticle(emitterName);
        if (emitter == null) {
            return;
        }

        const pathPositions = this.getPathPositions(direction);
        const emitterPosition = new Point();
        emitter.emit = true;
        const x = pathPositions.map((position) => position[orientation].x - emitter.parent[orientation].x);
        const y = pathPositions.map((position) => position[orientation].y - emitter.parent[orientation].y);
        const tween = new Tween(emitterPosition);
        tween.to({ x, y }, 1000);
        tween.repeat(Infinity);
        tween.start();
        tween.onUpdate(() => {
            emitter.updateSpawnPos(emitterPosition.x, emitterPosition.y);
        });
        emitter.resetPositionTracking();
        this.borderTweens.push(tween);
    }

    protected getPathPositions(direction: string): DualPosition[] {
        const pathPointIds = [0, 1, 2];
        return pathPointIds.map((index) => {
            return this.bigWinUiLayer.getPosition(`border_${direction}_path_${index}`);
        });
    }

    protected formatFunction(value: number): string {
        return Services.get(CurrencyService).format(value, !gameiomModel.shouldDisplayCredits(), gameiomModel.shouldDisplayCredits());
    }
}
